/**
 * SignaturePad Component
 * Componente para capturar firma digital con mouse o touch
 */
import React, { useRef, useEffect, useState } from 'react';
import './SignaturePad.css';

const SignaturePad = ({ onSave, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Configurar tamaño del canvas
    const resizeCanvas = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      context.scale(ratio, ratio);
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.lineWidth = 2;
      context.strokeStyle = '#000000';
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  const getCoordinates = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (event.touches) {
      const touch = event.touches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    } else {
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    }
  };

  const startDrawing = (event) => {
    event.preventDefault();
    setIsDrawing(true);
    setIsEmpty(false);
    
    const coords = getCoordinates(event);
    const context = canvasRef.current.getContext('2d');
    context.beginPath();
    context.moveTo(coords.x, coords.y);
  };

  const draw = (event) => {
    if (!isDrawing) return;
    event.preventDefault();
    
    const coords = getCoordinates(event);
    const context = canvasRef.current.getContext('2d');
    context.lineTo(coords.x, coords.y);
    context.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
    if (onClear) onClear();
  };

  const save = () => {
    if (isEmpty) {
      alert('Por favor, agregue su firma antes de continuar');
      return;
    }
    
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    if (onSave) onSave(dataUrl);
  };

  return (
    <div className="signature-pad-container">
      <div className="signature-pad-header">
        <h4>Firma Digital</h4>
        <p className="text-muted small">Dibuje su firma en el recuadro usando el mouse o su dedo</p>
      </div>
      
      <div className="signature-pad-canvas-wrapper">
        <canvas
          ref={canvasRef}
          className="signature-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {isEmpty && (
          <div className="signature-placeholder">
            Firme aquí
          </div>
        )}
      </div>

      <div className="signature-pad-actions">
        <button 
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={clear}
          disabled={isEmpty}
        >
          🗑️ Limpiar
        </button>
        <button 
          type="button"
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={isEmpty}
        >
          ✓ Guardar Firma
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
